import React from 'react';
import { Shield, User, Eye, Download, Trash2, Lock, Database, CheckCircle } from 'lucide-react';

const GDPR: React.FC = () => {
  const dataRights = [
    {
      icon: Eye,
      title: "Right to Access",
      description: "You have the right to request access to your personal data and information about how we process it."
    },
    {
      icon: Download,
      title: "Right to Portability",
      description: "You can request a copy of your personal data in a structured, machine-readable format."
    },
    {
      icon: User,
      title: "Right to Rectification",
      description: "You can request correction of inaccurate or incomplete personal data we hold about you."
    },
    {
      icon: Trash2,
      title: "Right to Erasure",
      description: "You can request deletion of your personal data in certain circumstances (also known as 'right to be forgotten')."
    },
    {
      icon: Lock,
      title: "Right to Restriction",
      description: "You can request that we limit how we process your personal data in certain situations."
    },
    {
      icon: Database,
      title: "Right to Object",
      description: "You can object to our processing of your personal data for specific purposes."
    }
  ];

  const dataCategories = [
    {
      category: "Identity Data",
      examples: ["Name, email address, phone number", "Profile information, preferences"],
      purpose: "Account creation, authentication, communication",
      retention: "As long as your account is active"
    },
    {
      category: "Usage Data",
      examples: ["Platform interactions, feature usage", "Login times, session data"],
      purpose: "Service improvement, security, analytics",
      retention: "2 years"
    },
    {
      category: "Financial Data",
      examples: ["Payment information, subscription details", "Billing history, transaction records"],
      purpose: "Payment processing, subscription management",
      retention: "7 years (legal requirement)"
    },
    {
      category: "Technical Data",
      examples: ["IP address, browser type, device info", "Cookies, analytics data"],
      purpose: "Security, performance optimization, analytics",
      retention: "1-2 years"
    }
  ];

  const securityMeasures = [
    "End-to-end encryption for all data transmission",
    "Regular security audits and penetration testing",
    "Access controls and role-based permissions",
    "Data backup and disaster recovery procedures",
    "Employee training on data protection",
    "Incident response and breach notification procedures"
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-40 bg-gradient-to-br from-startup-navy via-startup-teal to-startup-peach text-white animate-fade-in">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:50px_50px]" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex justify-center mb-8">
              <div className="w-20 h-20 border border-white/20 rounded-full flex items-center justify-center">
                <Shield className="w-10 h-10 text-white" />
              </div>
            </div>
            
            <h1 className="text-6xl md:text-7xl font-light tracking-tight mb-8 leading-tight">
              GDPR Compliance
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed max-w-2xl mx-auto font-light">
              Your data protection rights and how we ensure compliance with the General Data Protection Regulation
            </p>
            
            <div className="flex items-center justify-center gap-2 text-gray-400">
              <Shield className="w-5 h-5" />
              <span>Last updated: August 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* What is GDPR */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8 text-center">What is GDPR?</h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed text-center">
              The General Data Protection Regulation (GDPR) is a comprehensive data protection law that gives you 
              control over your personal data and ensures organizations handle your information responsibly.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-8">
              <h3 className="text-2xl font-medium text-blue-900 mb-4">Key Principles</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-blue-800 mb-2">Lawfulness, Fairness & Transparency</h4>
                  <p className="text-blue-700 text-sm">We process your data legally, fairly, and transparently</p>
                </div>
                <div>
                  <h4 className="font-medium text-blue-800 mb-2">Purpose Limitation</h4>
                  <p className="text-blue-700 text-sm">We only collect data for specific, legitimate purposes</p>
                </div>
                <div>
                  <h4 className="font-medium text-blue-800 mb-2">Data Minimization</h4>
                  <p className="text-blue-700 text-sm">We only collect data that is necessary and relevant</p>
                </div>
                <div>
                  <h4 className="font-medium text-blue-800 mb-2">Accuracy</h4>
                  <p className="text-blue-700 text-sm">We keep your data accurate and up-to-date</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Rights */}
      <section className="py-32 bg-gray-50 text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8 text-center">Your Data Protection Rights</h2>
            <p className="text-xl text-gray-600 mb-16 leading-relaxed text-center max-w-3xl mx-auto">
              Under GDPR, you have several important rights regarding your personal data. Here's what they mean:
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {dataRights.map((right, index) => (
                <div key={index} className="bg-white p-8 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                    <right.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-medium mb-4 text-black text-center">{right.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-center">
                    {right.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Data Categories */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8 text-center">What Data We Collect</h2>
            <p className="text-xl text-gray-600 mb-16 leading-relaxed text-center max-w-3xl mx-auto">
              We're transparent about what personal data we collect and how we use it:
            </p>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {dataCategories.map((category, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-8 hover:shadow-lg transition-shadow">
                  <h3 className="text-2xl font-medium text-black mb-4">{category.category}</h3>
                  
                  <div className="mb-6">
                    <h4 className="font-medium text-black mb-2">Examples:</h4>
                    <ul className="space-y-1">
                      {category.examples.map((example, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-gray-600">
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                          {example}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="font-medium text-black mb-2">Purpose:</h4>
                    <p className="text-gray-600">{category.purpose}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-black mb-2">Retention Period:</h4>
                    <p className="text-gray-600">{category.retention}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Legal Basis */}
      <section className="py-32 bg-gray-50 text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8 text-center">Legal Basis for Processing</h2>
            <p className="text-xl text-gray-600 mb-16 leading-relaxed text-center">
              We only process your personal data when we have a legal basis to do so:
            </p>
            
            <div className="space-y-8">
              <div className="bg-white p-8 rounded-lg border border-gray-200">
                <h3 className="text-2xl font-medium text-black mb-4">Contract Performance</h3>
                <p className="text-gray-600 leading-relaxed">
                  We process your data to provide our services, manage your account, and fulfill our contractual obligations.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-lg border border-gray-200">
                <h3 className="text-2xl font-medium text-black mb-4">Legitimate Interest</h3>
                <p className="text-gray-600 leading-relaxed">
                  We process data for business purposes like improving our services, security, and fraud prevention, 
                  always balancing our interests with your rights.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-lg border border-gray-200">
                <h3 className="text-2xl font-medium text-black mb-4">Consent</h3>
                <p className="text-gray-600 leading-relaxed">
                  For marketing communications and non-essential cookies, we rely on your explicit consent, 
                  which you can withdraw at any time.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-lg border border-gray-200">
                <h3 className="text-2xl font-medium text-black mb-4">Legal Obligation</h3>
                <p className="text-gray-600 leading-relaxed">
                  We may process data to comply with legal requirements, such as tax laws and financial regulations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Measures */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8 text-center">How We Protect Your Data</h2>
            <p className="text-xl text-gray-600 mb-16 leading-relaxed text-center">
              We implement comprehensive security measures to protect your personal data:
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              {securityMeasures.map((measure, index) => (
                <div key={index} className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <p className="text-gray-700 leading-relaxed">{measure}</p>
                </div>
              ))}
            </div>
            
            <div className="mt-12 text-center">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-2xl mx-auto">
                <h3 className="text-lg font-medium text-green-900 mb-3">Security Commitment</h3>
                <p className="text-green-800 leading-relaxed">
                  We regularly review and update our security practices to ensure your data remains protected 
                  against evolving threats and vulnerabilities.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* International Transfers */}
      <section className="py-32 bg-gray-50 text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8 text-center">International Data Transfers</h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed text-center">
              Your data may be transferred to and processed in countries outside the European Economic Area (EEA):
            </p>
            
            <div className="bg-white p-8 rounded-lg border border-gray-200">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-medium text-black mb-3">Adequacy Decisions</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We only transfer data to countries that the European Commission has deemed to provide 
                    an adequate level of data protection.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-medium text-black mb-3">Standard Contractual Clauses</h3>
                  <p className="text-gray-600 leading-relaxed">
                    For transfers to countries without adequacy decisions, we use EU-approved standard contractual 
                    clauses to ensure your data is protected.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-medium text-black mb-3">Your Rights</h3>
                  <p className="text-gray-600 leading-relaxed">
                    You have the same rights regarding international transfers as you do for data processed within the EEA.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exercising Your Rights */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8 text-center">How to Exercise Your Rights</h2>
            <p className="text-xl text-gray-600 mb-16 leading-relaxed text-center">
              Exercising your GDPR rights is simple and free:
            </p>
            
            <div className="space-y-8">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-8">
                <h3 className="text-2xl font-medium text-blue-900 mb-4">Contact Our Data Protection Officer</h3>
                <p className="text-blue-800 mb-6 leading-relaxed">
                  Email us at <strong>dpo@startupstreak.com</strong> with your request. 
                  We'll respond within 30 days.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-blue-900 mb-2">What to Include:</h4>
                    <ul className="space-y-1 text-blue-800 text-sm">
                      <li>• Your full name and contact details</li>
                      <li>• Specific right you want to exercise</li>
                      <li>• Any relevant account information</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-blue-900 mb-2">Response Time:</h4>
                    <p className="text-blue-800 text-sm">
                      We'll acknowledge your request within 48 hours and provide a full response within 30 days.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
                <h3 className="text-2xl font-medium text-black mb-4">Account Settings</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  You can also manage many of your data preferences directly in your account settings:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Update your profile information</li>
                  <li>• Manage communication preferences</li>
                  <li>• Control cookie settings</li>
                  <li>• Download your data</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact and Updates */}
      <section className="py-32 bg-gradient-to-br from-startup-navy via-startup-teal to-startup-peach text-white animate-fade-in">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8">
              Questions About GDPR?
            </h2>
            <p className="text-xl md:text-2xl mb-12 text-gray-300 font-light">
              Our Data Protection Officer is here to help with any questions about your data rights
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
              <a href="mailto:dpo@startupstreak.com" className="inline-block">
                <button className="bg-startup-teal text-startup-navy hover:bg-startup-gold hover:text-startup-navy text-lg px-12 py-4 font-medium transition-all hover:ring-2 hover:ring-startup-gold/60">
                  Contact DPO
                </button>
              </a>
              <a href="/privacy" className="inline-block">
                <button className="border border-startup-teal text-white hover:bg-white hover:text-startup-navy hover:border-white text-lg px-12 py-4 font-medium transition-all">
                  View Privacy Policy
                </button>
              </a>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-2xl mx-auto mb-12">
              <div className="text-center">
                <div className="w-16 h-16 border border-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-medium mb-2">Data Protection Officer</h3>
                <p className="text-gray-400 text-sm">dpo@startupstreak.com</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 border border-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-medium mb-2">Privacy Team</h3>
                <p className="text-gray-400 text-sm">privacy@startupstreak.com</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 border border-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-medium mb-2">Security Issues</h3>
                <p className="text-gray-400 text-sm">security@startupstreak.com</p>
              </div>
            </div>
            
            <div className="pt-8 border-t border-gray-800">
              <p className="text-gray-400 text-sm">
                This GDPR information was last updated on August 15, 2025. 
                We may update this information to reflect changes in our practices or legal requirements.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GDPR;
